<template>
  <div
    class="modal upgrade-plan-modal"
    @click="hidePopUp"
    @blur="hidePopUp"
  >
    <div
      class="upgrade-plan-popup"
      @click.stop
    >
      <PopupHeader
        :title="translate(popupContent.title)"
        @close="hidePopUp"
      />

      <span class="text-text-default px-6 py-1 text-sm">{{
        translate(popupContent.description)
      }}</span>
      <div class="flex flex-row px-6 py-6">
        <ButtonCustom
          class="text-sm font-normal rounded border-important-border flex-grow"
          :text="translate('cancel')"
          :type="'popup-button-wrap important'"
          @click="hidePopUp"
        />
        <ButtonCustom
          v-tooltip="{
            content: translate('upgradePlanBadgeTooltip', { spaceOwner: owner?.display_name }),
            placement: 'top',
            onShow: () => !isMobile && !isElevated,
          }"
          class="text-sm font-normal rounded border-important-border flex-grow ml-1"
          :text="translate('upgradePlan')"
          :type="'popup-button-wrap attention'"
          @click="routeToUpgradePlan"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useTranslate from '@/utilities/composables/useTranslate';
import PopupHeader from '@/components/elements/PopupHeader';
import ButtonCustom from '@/components/elements/ButtonCustom';
import constants from '@/utilities/constants';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import { onMounted, onUnmounted, computed } from 'vue';
import { TeamMemberRole } from '@superthread-com/api';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const { translate } = useTranslate();
const store = useStore();
const router = useRouter();
const { isElevated } = useCurrentRole();

defineEmits(['closePopup']);

onMounted(() => {
  window.addEventListener('keyup', keypressHandler);
});

onUnmounted(() => {
  window.removeEventListener('keyup', keypressHandler);
});

function keypressHandler(e) {
  if (e.code === 'Enter') {
    routeToUpgradePlan();
  }
  if (e.code === 'Escape') {
    e.stopPropagation();
    hidePopUp();
  }
}

function hidePopUp() {
  store.dispatch('setShowUpgradePlanPopup', null);
}

const currentRole = computed(() => {
  return store.getters.getCurrentRole;
});

const userHasAccessToBilling = computed(() => {
  return [TeamMemberRole.Owner, TeamMemberRole.Admin].includes(currentRole.value);
});

const owner = computed(() => {
  return store.getters.getMemberById(store.getters.getWorkspaceOwner?.id);
});

function routeToUpgradePlan() {
  if (!userHasAccessToBilling.value) return;
  router.push({
    name: constants.routeNames.billing,
  });
  store.dispatch('setShowUpgradePlanPopup', null);
  if (store.getters.getAddMemberPopupIsOn) {
    store.dispatch('setAddMemberPopup', false);
  }
}

const popupContent = computed(() => {
  const { featureFlagIds: ffIds } = constants;
  const contentMap = {
    [ffIds.privateSpace]: {
      title: 'upgradePrivateSpaceTitle',
      description: 'upgradePrivateSpaceDescription',
    },
    [ffIds.userPermissions]: {
      title: 'upgradeUserRolesTitle',
      description: 'upgradeUserRolesDescription',
    },
    [ffIds.publicPageIndexing]: {
      title: 'upgradePublicPageIndexingTitle',
      description: 'upgradePublicPageIndexingDescription',
    },
    [ffIds.timelineLayout]: {
      title: 'upgradePlan',
      description: 'timelineLayoutDescription',
    },
    [ffIds.calendarLayout]: {
      title: 'upgradePlan',
      description: 'calendarLayoutDescription',
    },
    [ffIds.views]: {
      title: 'upgradePlan',
      description: 'upgradeViewsDescription',
    },
    [ffIds.cardAndPageTemplates]: {
      title: 'upgradePlan',
      description: 'upgradeTemplatesDescription',
    },
    [ffIds.roadmapAndProjects]: {
      title: 'upgradePlan',
      description: 'upgradeRoadmapAndProjectsDescription',
    },
  };
  return contentMap[store.getters.getShowUpgradePlanPopup] || { title: '', description: '' };
});
</script>

<style lang="scss">
.upgrade-plan-modal {
  z-index: 101;

  .upgrade-plan-popup {
    @apply flex flex-col w-[400px] box-border bg-background-strongest rounded-lg border border-solid border-important-border;
  }
}

.mobile {
  .upgrade-plan-popup {
    @apply w-[300px];
  }
}
</style>
